import React, { useEffect, useRef } from 'react';
import { announceToScreenReader } from '../../utils/accessibility';
import './TopTransportBar.css';

/**
 * Props for TopTransportBar component
 * 
 * Requirements: 3.1, 3.2, 3.3, 3.5, 3.7, 3.9, 16.1, 16.2, 16.3, 16.5
 */
export interface TopTransportBarProps {
  /** Whether audio is currently playing */
  isPlaying: boolean;
  /** Whether audio is paused */
  isPaused: boolean;
  /** Whether loop mode is enabled */
  loopEnabled: boolean;
  /** Whether the grid has any active notes */
  hasNotes: boolean;
  /** Beats per minute (tempo) */
  bpm: number;
  /** Number of steps in the composition */
  steps: number;
  /** Play callback */
  onPlay: () => void;
  /** Pause callback */
  onPause: () => void;
  /** Stop callback */
  onStop: () => void;
  /** Toggle loop callback */
  onToggleLoop: () => void;
  /** BPM tap callback - opens BPM picker modal */
  onBPMTap: () => void;
  /** Steps tap callback - opens Steps picker modal */
  onStepsTap: () => void;
  /** Menu tap callback - displays secondary actions */
  onMenuTap: () => void;
  /** Save indicator state - shows badge when composition is saved */
  saveIndicator?: string;
}

/**
 * TopTransportBar Component
 * 
 * Compact playback controls for mobile layout, displayed at the top of the screen.
 * Provides Play/Pause toggle, Stop button, Loop toggle, BPM display, Steps display, and Menu button.
 * All buttons meet the 44x44px minimum touch target size for accessibility.
 * 
 * Requirements: 3.1, 3.2, 3.3, 3.5, 3.7, 3.9, 16.1, 16.2, 16.3, 16.5
 */
export const TopTransportBar: React.FC<TopTransportBarProps> = ({
  isPlaying,
  isPaused,
  loopEnabled,
  hasNotes,
  bpm,
  steps,
  onPlay,
  onPause,
  onStop,
  onToggleLoop,
  onBPMTap,
  onStepsTap,
  onMenuTap,
  saveIndicator,
}) => {
  const previousPlayingRef = useRef<boolean>(isPlaying);

  /**
   * Announce playback state changes to screen readers
   * Requirement 16.5: Screen reader announcements for state changes
   */
  useEffect(() => {
    if (previousPlayingRef.current !== isPlaying) {
      if (isPlaying && !isPaused) {
        announceToScreenReader('Playback started');
      } else if (!isPlaying) {
        announceToScreenReader('Playback stopped');
      } else if (isPaused) {
        announceToScreenReader('Playback paused');
      }
      previousPlayingRef.current = isPlaying;
    }
  }, [isPlaying, isPaused]);

  /**
   * Handle play/pause toggle
   * Requirement 3.1: Display Play/Pause toggle button
   */
  const handlePlayPauseClick = () => {
    if (isPlaying && !isPaused) {
      onPause();
    } else {
      onPlay();
    }
  };

  /**
   * Handle keyboard shortcuts
   * Requirement 16.3: Keyboard navigation support
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Space bar for play/pause
    if (e.key === ' ' && e.target === e.currentTarget) {
      e.preventDefault();
      handlePlayPauseClick();
    }
  };

  return (
    <div 
      className="top-transport-bar" 
      role="toolbar" 
      aria-label="Playback controls"
      onKeyDown={handleKeyDown}
    >
      {/* Play/Pause Toggle Button - Requirement 3.1 */}
      <button
        className="transport-btn transport-btn-play-pause"
        onClick={handlePlayPauseClick}
        aria-label={isPlaying && !isPaused ? 'Pause' : 'Play'}
        title={isPlaying && !isPaused ? 'Pause playback' : 'Play composition'}
      >
        <i className={`fa-solid ${isPlaying && !isPaused ? 'fa-pause' : 'fa-play'}`} aria-hidden="true"></i>
      </button>

      {/* Stop Button - Requirement 3.2 */}
      <button
        className="transport-btn transport-btn-stop"
        onClick={onStop}
        disabled={!isPlaying && !isPaused}
        aria-label="Stop"
        title="Stop playback"
      >
        <i className="fa-solid fa-stop" aria-hidden="true"></i>
      </button>

      {/* Loop Toggle Button */}
      <button
        className={`transport-btn transport-btn-loop ${loopEnabled ? 'transport-btn-loop-active' : ''}`}
        onClick={() => {
          console.log('[TopTransportBar] Loop button clicked, current state:', loopEnabled);
          onToggleLoop();
        }}
        disabled={!hasNotes}
        aria-label={loopEnabled ? 'Loop enabled' : 'Loop disabled'}
        aria-pressed={loopEnabled}
        title={!hasNotes ? 'Add notes to enable loop' : loopEnabled ? 'Disable loop mode' : 'Enable loop mode'}
      >
        <i className="fa-solid fa-sync-alt" aria-hidden="true"></i>
      </button>

      {/* BPM Display - Requirement 3.3, 3.5 */}
      <button
        className="transport-display transport-display-bpm"
        onClick={onBPMTap}
        aria-label={`BPM: ${bpm}. Tap to change`}
        title="Tap to change BPM"
      >
        <span className="transport-display-label" aria-hidden="true">BPM</span>
        <span className="transport-display-value">{bpm}</span>
      </button>

      {/* Steps Display - Requirement 3.3, 3.5 */}
      <button
        className="transport-display transport-display-steps"
        onClick={onStepsTap}
        aria-label={`Steps: ${steps}. Tap to change`}
        title="Tap to change steps"
      >
        <span className="transport-display-label" aria-hidden="true">Steps</span>
        <span className="transport-display-value">{steps}</span>
      </button>

      {/* Menu Button - Requirement 3.7, 3.8 */}
      <button
        className="transport-btn transport-btn-menu"
        onClick={onMenuTap}
        aria-label="Song settings"
        title="Open song settings"
        style={{ position: 'relative' }}
      >
        <i className="fa-solid fa-gear" aria-hidden="true"></i>
        {/* Save indicator badge */}
        {saveIndicator && (
          <span 
            className="save-badge"
            style={{
              position: 'absolute',
              top: '6px',
              right: '6px',
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: '#00ff00',
              boxShadow: '0 0 8px #00ff00',
              animation: 'pulse 0.6s ease-in-out',
              pointerEvents: 'none'
            }}
            aria-label="Composition saved"
          />
        )}
      </button>
    </div>
  );
};
